import type {
  Invoice,
  InvoiceBulkFilter,
  InvoiceBulkTarget,
  InvoiceFlag,
  InvoiceListQuery,
  InvoiceScheduleSend,
} from '@clockwise/clients/invoices';
import {
  ApiError,
  compareMoney,
  INVOICE_STATUS_LABELS,
  INVOICE_STATUSES,
  INVOICE_TYPE_LABELS,
  INVOICE_TYPES,
  type InvoiceStatus,
  isMoney,
  peakInvoiceFlagSeverity,
  percentChangeOfMoney,
} from '@clockwise/shared';
import { DateRangePopover, type DateRangeValue, resolveDateRange } from '@clockwise/ui';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CalendarClock,
  CalendarDays,
  CheckCheck,
  Download,
  Filter,
  History,
  MoreHorizontal,
  Plus,
  ReceiptText,
  Send,
  Settings,
  Sparkles,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { type BulkAction, BulkActionBar } from '@/components/custom/bulk-action-bar';
import { EntityPicker } from '@/components/custom/entity-picker';
import { customerPickerConfig } from '@/components/custom/entity-pickers';
import { GenerateInvoicesSheet } from '@/components/custom/generate-invoices-sheet';
import {
  InvoiceEmailBadge,
  InvoiceFlagBadge,
  InvoiceStatusBadge,
  InvoiceTypeBadge,
} from '@/components/custom/invoice-badges';
import {
  actionCounts,
  type InvoiceSelection,
  idsWithStatus,
  isRowSelected,
  isSelectable,
  NOTHING_SELECTED,
  pageSelectionState,
  runBulkInChunks,
  runBulkQueueInChunks,
  selectPage as selectPageRows,
  summarizeFailures,
  targetForStatuses,
  toggleRow as toggleSelectionRow,
} from '@/components/custom/invoice-bulk-selection';
import { InvoiceBulkSendDialog } from '@/components/custom/invoice-bulk-send-dialog';
import { InvoiceDateDialog } from '@/components/custom/invoice-date-dialog';
import { InvoiceFlagDetails } from '@/components/custom/invoice-flag-details';
import { formatMoney } from '@/components/custom/invoice-money';
import { InvoiceRunProgress } from '@/components/custom/invoice-run-progress';
import { InvoiceScheduleSendDialog } from '@/components/custom/invoice-schedule-send-dialog';
import { canSendInvoice, InvoiceSendDialog } from '@/components/custom/invoice-send-dialog';
import { SearchInput } from '@/components/custom/search-input';
import { SelectionCheckbox } from '@/components/custom/selection-checkbox';
import { StickyTableScroll } from '@/components/custom/sticky-table-scroll';
import { UnbilledCustomersPanel } from '@/components/custom/unbilled-customers-panel';
import { PageHeader } from '@/components/layout/page-header';
import { PageShell } from '@/components/layout/page-shell';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useElementWidth } from '@/hooks/use-element-width';
import { invoicesApi } from '@/lib/api';
import { hiddenColumnsForWidth, minTableWidthPx } from '@/lib/columns-that-fit';
import { dateRangePresetSchema } from '@/lib/date-range-search';
import { triggerDownload } from '@/lib/download';
import { useInvoiceFlags } from '@/lib/invoice-run-hooks';
import { useHasPermission } from '@/lib/permissions';
import { queryKeys } from '@/lib/query-keys';
import { cn } from '@/lib/utils';

const SORT_FIELD_VALUES = [
  'invoiceDate',
  'invoiceNumber',
  'billToName',
  'status',
  'invoiceType',
  'total',
  'dueDate',
  // The review columns. Every sortable COLUMN must appear here: the table
  // renders a sort toggle on any column with an `accessorKey`, the toggle
  // writes the field into the URL, and a value this enum rejects fails the
  // route's search validation and blanks the whole page.
  'priorTotal',
  'reviewFlagCount',
  'emailStatus',
] as const;
type SortField = (typeof SORT_FIELD_VALUES)[number];

/**
 * URL state. Every filter lives here rather than in component state, so a
 * finance user can bookmark "IMIDomics, approved, this quarter" and mail the
 * link to whoever has to act on it (G13).
 *
 * The date range is stored as a preset plus optional custom bounds rather than
 * as two resolved dates: a link that says "current quarter" should still mean
 * the current quarter when it is opened next month.
 */
const listSearch = z.object({
  q: z.string().optional(),
  customerIds: z.array(z.string().uuid()).optional(),
  status: z.enum(INVOICE_STATUSES).optional(),
  invoiceType: z.enum(INVOICE_TYPES).optional(),
  /** Whether to show only flagged invoices, only unflagged, or (absent) both.
   *  A tri-state in the URL rather than a boolean so "no opinion" survives a
   *  bookmark — `flagged=false` means "show me the clean ones", which is a
   *  different question from not filtering at all. */
  flagged: z.enum(['true', 'false']).optional(),
  /** Only invoices raised by one generation run — how the run page's Flagged
   *  tile lands here. */
  generationRunId: z.string().uuid().optional(),
  datePreset: dateRangePresetSchema.optional(),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  page: z.coerce.number().int().min(0).optional(),
  sortField: z.enum(SORT_FIELD_VALUES).optional(),
  sortDirection: z.enum(['asc', 'desc']).optional(),
  /**
   * The generate sheet's open state and selections.
   *
   * In the URL so the surface is ADDRESSABLE: the sheet links out to the
   * variable-expense grid, and the grid can only offer a way back to an open
   * sheet — with the same month and filters — if that state is something a
   * link can carry. It also makes the browser's back button do the obvious
   * thing instead of leaving the user stranded on the grid.
   */
  generate: z.boolean().optional(),
  gMonth: z.string().optional(),
  gFreqs: z.string().optional(),
  /** Which engine the sheet had selected. Validated in the sheet, not here:
   *  an unknown value falls back to the default rather than 400-ing a page. */
  gKind: z.string().optional(),
});
type ListSearch = z.infer<typeof listSearch>;

export const Route = createFileRoute('/_authenticated/invoices/')({
  validateSearch: listSearch,
  component: InvoicesPage,
});

const PAGE_SIZE = 25;
const DEFAULT_SORT_FIELD: SortField = 'invoiceDate';
const DEFAULT_SORT_DIRECTION = 'desc';
const ALL = 'all';

/** Presets offered in the date filter. `all_time` leads because the list's
 *  default is "everything" — a finance user opening the page wants the whole
 *  ledger, not the current month. */
const DATE_PRESETS = [
  'all_time',
  'current_month',
  'previous_month',
  'current_quarter',
  'previous_quarter',
  'current_year',
  'previous_year',
  'custom',
] as const;

/**
 * Fixed column widths, in px. The table is `table-layout: fixed`, so the
 * header row alone decides them — under the default auto layout the browser
 * re-measures on every data change and the columns visibly jump between the
 * skeleton, the loaded page, and each subsequent page.
 *
 * `billToName` is deliberately absent: the unsized column absorbs the
 * remaining space, so the flexible one should be the most variable. It must be
 * the ONLY unsized column — `table-fixed` splits the leftover evenly across
 * every unsized column, so an unsized Previous/Flags/Send each took a share of
 * it, truncating Customer and leaving a wide blank band on the right.
 *
 * Numbers rather than Tailwind classes because `hiddenColumnsForWidth` adds
 * them up to decide which columns a narrow container can still hold.
 */
const COLUMN_WIDTHS_PX: Record<string, number> = {
  select: 40,
  invoiceNumber: 128,
  invoiceDate: 112,
  dueDate: 112,
  // Wide enough for "Time & materials", the longest type label. Sized to the
  // label rather than to the average, because a badge that overflows its
  // column lands on top of the Status chip beside it.
  invoiceType: 160,
  status: 112,
  // Fits "$12,345.67 +12.3%", a prior total plus its delta.
  priorTotal: 176,
  total: 128,
  // Fits "Email sent", the longest email chip, and no more: every pixel here
  // comes out of the flexible Customer column.
  emailStatus: 128,
  // One flag chip, or a dash.
  reviewFlagCount: 80,
  // One icon button.
  send: 48,
};

/** Below this, Customer is too truncated to tell invoices apart. */
const CUSTOMER_MIN_PX = 240;

/**
 * What a narrowing container drops, least important first. Previous repeats
 * what the AMOUNT_CHANGED flag says; Due is Date plus terms; Email and Type
 * are one click away on the invoice. Invoice #, Customer, Status and Total
 * never go — they are the row. Email outlives Due so the 1280px e2e viewport
 * keeps it.
 */
const HIDE_ORDER = [
  'priorTotal',
  'dueDate',
  'emailStatus',
  'invoiceType',
  'invoiceDate',
  'reviewFlagCount',
] as const;

function InvoicesPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const queryClient = useQueryClient();
  const canEdit = useHasPermission('invoice.edit');
  const generateOpen = search.generate === true;
  /** Opening and closing is a navigation, because the open state lives in the
   *  URL. Closing clears the selections too, so a later open starts clean
   *  rather than resurrecting a month someone picked an hour ago. */
  const setGenerateOpen = (next: boolean) =>
    navigate({
      search: (prev: ListSearch) =>
        next
          ? { ...prev, generate: true }
          : {
              ...prev,
              generate: undefined,
              gMonth: undefined,
              gFreqs: undefined,
              gKind: undefined,
            },
      replace: true,
    });
  // The run being watched by the progress toast. Held here rather than in the
  // dialog because the dialog closes the moment the run is queued — the toast
  // has to outlive it.
  const [watchedRunId, setWatchedRunId] = useState<string | null>(null);
  const canViewInvoiceSettings = useHasPermission('invoice.settings.view');
  const canViewVariableExpenses = useHasPermission('customer.expense.view');
  // Approving is a financial sign-off with its own permission — someone who may
  // fix a typo in a draft does not thereby get to sign off two thousand of them.
  const canApprove = useHasPermission('invoice.approve');
  // Sending puts a document in a customer's inbox — its own permission again.
  // Someone who may send can also tick SENT rows: sending again is the one
  // thing that can still be done to them.
  const canSend = useHasPermission('invoice.send');
  /** Only rows some action of this person's can touch are tickable. */
  const selectionRules = useMemo(
    () => ({ drafts: canApprove || canEdit, sent: canSend }),
    [canApprove, canEdit, canSend],
  );
  /** The row whose Send dialog is open. */
  const [sendRow, setSendRow] = useState<Invoice | null>(null);

  const sortField: SortField = search.sortField ?? DEFAULT_SORT_FIELD;
  const sortDirection = search.sortDirection ?? DEFAULT_SORT_DIRECTION;
  const page = search.page ?? 0;
  const q = search.q ?? '';
  const sorting: SortingState = [{ id: sortField, desc: sortDirection === 'desc' }];

  const dateRange: DateRangeValue = {
    preset: search.datePreset ?? 'all_time',
    from: search.dateFrom,
    to: search.dateTo,
  };
  const resolvedRange = resolveDateRange(dateRange);
  /**
   * Which month the "not invoiced" panel answers for.
   *
   * The list's own date filter when it has one, so the panel talks about what
   * the user is looking at; otherwise the current month. Pinned to the first —
   * the API matches `invoice_month` by equality, and an unpinned value would
   * silently return nothing.
   *
   * Deliberately not "every month with a problem": this panel sits above one
   * filtered list and must describe it. The daily integrity email is what
   * covers the months nobody is currently looking at.
   */
  const unbilledMonth = `${(resolvedRange?.from ?? new Date().toISOString().slice(0, 10)).slice(0, 7)}-01`;

  const updateSearch = (next: Partial<ListSearch>) =>
    navigate({ search: (prev: ListSearch) => stripDefaults({ ...prev, ...next }), replace: true });

  // The search box debounces before committing to the URL, so the typed value
  // and the URL value diverge for 300ms. Both are kept: the input renders the
  // typed one, the query reads the committed one.
  const [searchInput, setSearchInput] = useState(q);
  useEffect(() => {
    setSearchInput(q);
  }, [q]);
  useEffect(() => {
    if (searchInput === q) return;
    const handle = setTimeout(() => updateSearch({ q: searchInput, page: 0 }), 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, q]);

  const filters = {
    q: q.trim() || undefined,
    customerIds: search.customerIds,
    status: search.status,
    invoiceType: search.invoiceType,
    flagged: search.flagged === undefined ? undefined : search.flagged === 'true',
    generationRunId: search.generationRunId,
    dateFrom: resolvedRange?.from,
    dateTo: resolvedRange?.to,
    sortField,
    sortDirection,
  } satisfies Omit<InvoiceListQuery, 'limit' | 'offset'>;

  const listQuery: InvoiceListQuery = {
    ...filters,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  };

  /**
   * The filter as the bulk endpoints take it: the predicate, and nothing about
   * ordering or which page was on screen. Sending `sortField` would imply the
   * order changed what got approved.
   */
  const bulkFilter: InvoiceBulkFilter = {
    q: filters.q,
    customerIds: filters.customerIds,
    status: filters.status,
    invoiceType: filters.invoiceType,
    flagged: filters.flagged,
    generationRunId: filters.generationRunId,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
  };

  const [selection, setSelection] = useState<InvoiceSelection>(NOTHING_SELECTED);
  const selectionActive =
    selection.mode === 'filter' || (selection.mode === 'ids' && selection.rows.size > 0);

  // A changed filter invalidates the selection outright. Ids ticked under the
  // old filter may no longer match it, and a filter-mode selection would
  // silently re-point at a different set of invoices than the one the user was
  // shown a count for. Paging and sorting are excluded on purpose — both keep
  // the matching set identical, and dropping a cross-page selection on the way
  // to page 2 would make cross-page selection useless.
  const filterKey = JSON.stringify(bulkFilter);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setSelection(NOTHING_SELECTED), [filterKey]);

  const query = useQuery({
    queryKey: queryKeys.invoices.list(listQuery),
    queryFn: () => invoicesApi.list(listQuery),
    // Keeps the previous page on screen while the next one loads, so paging
    // doesn't blink the table back to a skeleton.
    placeholderData: keepPreviousData,
  });

  // Export mirrors the on-screen filters exactly — what you see is what you
  // get in the file. Reads `searchInput` (the typed value) rather than URL
  // state `q`, because clicking Export inside the 300ms debounce window would
  // otherwise export the *previous* query.
  const exportMutation = useMutation({
    mutationFn: () => invoicesApi.exportXlsx({ ...filters, q: searchInput.trim() || undefined }),
    onSuccess: ({ blob, filename }) => triggerDownload(blob, filename ?? 'invoices.xlsx'),
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Export failed'),
  });

  /**
   * How many matching invoices sit in each status.
   *
   * Only fetched once something is selected: the bar is the only thing that
   * needs it, and the list itself re-runs on every keystroke of the search box.
   */
  const countsQuery = useQuery({
    queryKey: queryKeys.invoices.statusCounts(bulkFilter),
    queryFn: () => invoicesApi.statusCounts(bulkFilter),
    enabled: selectionActive,
    placeholderData: keepPreviousData,
  });
  const matching = countsQuery.data?.data;

  /**
   * How many rows each action would actually touch.
   *
   * In `ids` mode it comes from the statuses captured when the rows were
   * ticked; in `filter` mode from the server's counts, less whatever was
   * unticked afterwards. Never from "how many are selected" — a selection of 30
   * that holds 12 drafts approves 12, and a button that said 30 would be
   * promising something the server will not do.
   */
  const {
    approvable: approvableCount,
    schedulable: schedulableCount,
    sendable: sendableCount,
    selected: selectedCount,
  } = actionCounts(selection, matching, selectionRules);

  /** How many invoices a filter selection would actually cover — the
   *  statuses this person's actions reach (`selectionRules`). Undefined until
   *  the counts land. */
  const matchingActionable = matching
    ? (selectionRules.drafts ? matching.draft : 0) +
      matching.approved +
      (selectionRules.sent ? matching.sent : 0)
    : undefined;

  /** What to send for an action: the ticked ids in the right status, or the
   *  filter itself plus the rows the user dropped from it. */
  const targetFor = (status: InvoiceStatus): InvoiceBulkTarget =>
    selection.mode === 'filter'
      ? // Only the exclusions this action could have acted on: the server
        // constrains by status anyway, and a shorter list stays under the cap.
        { filter: bulkFilter, excludeIds: idsWithStatus(selection.excluded, status) }
      : { ids: idsWithStatus(selection.rows, status) };

  /** The selected drafts and approved invoices — for rescheduling, which
   *  reaches both. Never sent ones, even when they are ticked for sending. */
  const targetSchedulable = (): InvoiceBulkTarget =>
    targetForStatuses(selection, bulkFilter, ['draft', 'approved']);

  /** The selected approved (first send) and sent (resend) invoices. */
  const targetSendable = (): InvoiceBulkTarget =>
    targetForStatuses(selection, bulkFilter, ['approved', 'sent']);

  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [invoiceDateOpen, setInvoiceDateOpen] = useState(false);
  const [bulkSendOpen, setBulkSendOpen] = useState(false);

  /**
   * Queue the selected invoices for sending. Queued, not sent: the server
   * judges the selection again, hands the sendable ones to a background job,
   * and answers at once. The toast says what was queued and what was skipped;
   * each invoice moves to Sent (or shows why it failed) as it goes out.
   */
  const bulkSend = useMutation({
    mutationFn: (target: InvoiceBulkTarget) => {
      // One key per press for the whole selection, suffixed per chunk, so a
      // retried request cannot queue the same chunk twice.
      const key = crypto.randomUUID();
      let chunk = 0;
      return runBulkQueueInChunks(target, (t) =>
        invoicesApi.queueBulkSend(t, { idempotencyKey: `${key}:${chunk++}` }),
      );
    },
    onSuccess: ({ data }) => {
      const n = data.queued;
      const queued = `Sending ${n.toLocaleString()} ${n === 1 ? 'invoice' : 'invoices'} in the background`;
      if (data.skipped.length === 0) {
        toast.success(queued, {
          description: 'Each one moves to Sent as it goes out.',
        });
      } else {
        toast.warning(`${queued}; ${data.skipped.length.toLocaleString()} skipped`, {
          description: summarizeFailures(data.skipped),
          duration: Number.POSITIVE_INFINITY,
        });
      }
      setSelection(NOTHING_SELECTED);
      setBulkSendOpen(false);
      queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Could not queue these invoices', {
        description: 'Nothing was sent. Try again.',
      });
    },
  });

  const bulkMutation = useMutation({
    mutationFn: async ({
      action,
      target,
      schedule,
      invoiceDate,
    }: {
      // No Unlock here: unlocking clears a sign-off, so it is done one invoice
      // at a time from the invoice itself, and the API refuses a bulk unlock.
      action: 'approve' | 'schedule' | 'invoiceDate';
      target: InvoiceBulkTarget;
      /** Required for `schedule`, ignored otherwise. */
      schedule?: InvoiceScheduleSend;
      /** Required for `invoiceDate`, ignored otherwise. */
      invoiceDate?: string;
    }) => {
      const call = (t: InvoiceBulkTarget) =>
        action === 'approve'
          ? invoicesApi.approve(t)
          : action === 'invoiceDate'
            ? invoicesApi.setInvoiceDate(t, invoiceDate ?? '')
            : invoicesApi.scheduleSend(t, schedule ?? { sendMode: 'manual' });
      return runBulkInChunks(target, call);
    },
    onSuccess: ({ data }, { action }) => {
      const verb =
        action === 'approve' ? 'Approved' : action === 'invoiceDate' ? 'Re-dated' : 'Rescheduled';
      const ok = data.succeeded.length;
      if (data.failed.length === 0) {
        toast.success(`${verb} ${ok} ${ok === 1 ? 'invoice' : 'invoices'}`);
      } else {
        // Partial success is the expected outcome, not an error: one invoice
        // with no line items must not make the other 185 look like they failed.
        // The reasons are named — "skipped" with no why is a dead end.
        //
        // It stays until dismissed: a toast that fades in four seconds is how
        // a skip goes unnoticed.
        toast.warning(`${verb} ${ok}; ${data.failed.length} skipped`, {
          description: summarizeFailures(data.failed),
          duration: Number.POSITIVE_INFINITY,
        });
      }
      setSelection(NOTHING_SELECTED);
      queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
    },
    onError: (err) => {
      // A failed bulk is NOT a bulk that did nothing. The server commits in
      // chunks, so some of the selection may already be approved — saying
      // "could not update" and leaving a stale list on screen would send the
      // user to look for work that is already done. Refresh, clear the
      // selection (its captured statuses are now wrong), and say what to do.
      toast.error(
        err instanceof ApiError ? err.message : 'Could not finish updating these invoices',
        { description: 'Some may already have been updated. The list has been refreshed.' },
      );
      setSelection(NOTHING_SELECTED);
      queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
    },
  });

  const pageRows = query.data?.data ?? [];
  const pageSelectable = pageRows.filter((r) => isSelectable(r.status, selectionRules));

  /**
   * Flags for the rows on screen, fetched once for the page.
   *
   * Only for rows the cached count says carry any — asking about 25 ids to
   * learn about 2 is a wasted payload on the common page, and the count is
   * maintained in the same transaction as the flag rows so it can be trusted
   * to decide who to ask about.
   */
  const flaggedIds = useMemo(
    () => pageRows.filter((r) => r.reviewFlagCount > 0).map((r) => r.id),
    [pageRows],
  );
  const flagsQuery = useInvoiceFlags(flaggedIds);
  const flagsByInvoice = useMemo(() => {
    const out = new Map<string, InvoiceFlag[]>();
    for (const flag of flagsQuery.data?.data ?? []) {
      const list = out.get(flag.invoiceId);
      if (list === undefined) out.set(flag.invoiceId, [flag]);
      else list.push(flag);
    }
    return out;
  }, [flagsQuery.data]);

  const toggleRow = (row: Invoice, next: 'all' | 'none') =>
    setSelection((current) => toggleSelectionRow(current, row, next));

  const selectPage = () => setSelection(selectPageRows(pageRows, selectionRules));

  const pageState = pageSelectionState(selection, pageRows, selectionRules);

  const columns = useMemo<ColumnDef<Invoice>[]>(
    () => [
      // Selection leads the row, and only for someone who may approve or
      // send: a checkbox column that can do nothing is a column of dead
      // controls.
      ...(canApprove || canSend
        ? [
            {
              id: 'select',
              enableSorting: false,
              header: () => (
                <SelectionCheckbox
                  state={pageState}
                  label="Select every invoice on this page"
                  disabled={pageSelectable.length === 0}
                  onChange={(next) =>
                    next === 'all' ? selectPage() : setSelection(NOTHING_SELECTED)
                  }
                />
              ),
              cell: ({ row }: { row: { original: Invoice } }) => {
                const selectable = isSelectable(row.original.status, selectionRules);
                return (
                  <SelectionCheckbox
                    state={isRowSelected(selection, row.original, selectionRules) ? 'all' : 'none'}
                    disabled={!selectable}
                    label={
                      selectable
                        ? `Select invoice ${row.original.invoiceNumber}`
                        : `${row.original.invoiceNumber} has been sent and cannot be changed`
                    }
                    onChange={(next) => toggleRow(row.original, next)}
                  />
                );
              },
            } satisfies ColumnDef<Invoice>,
          ]
        : []),
      {
        accessorKey: 'invoiceNumber',
        header: 'Invoice #',
        // First column is the primary identifier and links to the record, per
        // the list-table convention in CONTRIBUTING.md.
        cell: ({ row }) => (
          <Link
            to="/invoices/$id"
            params={{ id: row.original.id }}
            className="font-medium text-sm tabular-nums hover:underline"
          >
            {row.original.invoiceNumber}
          </Link>
        ),
      },
      {
        accessorKey: 'billToName',
        header: 'Customer',
        // `block` is load-bearing: `truncate` sets overflow/text-overflow, and
        // CSS ignores both on a non-replaced INLINE box. As a plain span the
        // name never ellipsized and, under `table-fixed`, ran over into the
        // Date cell — the same overflow that widened `invoiceType` to `w-40`.
        cell: ({ row }) => (
          <span className="block truncate" title={row.original.billToName}>
            {row.original.billToName}
          </span>
        ),
      },
      {
        accessorKey: 'invoiceDate',
        header: 'Date',
        cell: ({ row }) => <DateCell iso={row.original.invoiceDate} />,
      },
      {
        accessorKey: 'dueDate',
        header: 'Due',
        cell: ({ row }) => <DateCell iso={row.original.dueDate} />,
      },
      {
        accessorKey: 'invoiceType',
        header: 'Type',
        cell: ({ row }) => <InvoiceTypeBadge type={row.original.invoiceType} />,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => <InvoiceStatusBadge status={row.original.status} />,
      },
      {
        // Immediately LEFT of Total, so the comparison is one saccade rather
        // than a mental join across the row.
        accessorKey: 'priorTotal',
        header: 'Previous',
        cell: ({ row }) => <PreviousCell invoice={row.original} />,
      },
      {
        accessorKey: 'total',
        header: 'Total',
        cell: ({ row }) => {
          const formatted = formatMoney(row.original.total, row.original.currency);
          // An unformattable total is a data gap, not a zero. Saying so keeps
          // a broken row from reading as a real $0.00 invoice (G15).
          return formatted === null ? (
            <span className="text-xs text-destructive">Unknown</span>
          ) : (
            <span className="text-sm tabular-nums">{formatted}</span>
          );
        },
      },
      {
        // Right of Total: what became of the email is the next question
        // after "how much". One chip — the last meaningful status — with
        // sent / delivered / opened details on hover, click or tap.
        accessorKey: 'emailStatus',
        header: 'Email',
        cell: ({ row }) => <InvoiceEmailBadge invoice={row.original} untracked="label" />,
      },
      {
        accessorKey: 'reviewFlagCount',
        header: 'Flags',
        cell: ({ row }) => (
          <FlagsCell
            invoice={row.original}
            flags={flagsByInvoice.get(row.original.id) ?? []}
            // Placeholder data is the PREVIOUS page's flags: after paging, the
            // query is not pending but these rows' flags have not arrived.
            pending={flagsQuery.isPending || flagsQuery.isPlaceholderData}
          />
        ),
      },
      ...(canSend
        ? [
            {
              id: 'send',
              enableSorting: false,
              header: () => <span className="sr-only">Send</span>,
              cell: ({ row }: { row: { original: Invoice } }) =>
                canSendInvoice(row.original.status) ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8"
                    onClick={() => setSendRow(row.original)}
                    aria-label={`${row.original.status === 'sent' ? 'Resend' : 'Send'} invoice ${row.original.invoiceNumber}`}
                    title={row.original.status === 'sent' ? 'Resend' : 'Send'}
                  >
                    <Send className="size-4" />
                  </Button>
                ) : null,
            } satisfies ColumnDef<Invoice>,
          ]
        : []),
    ],
    // The selection column closes over the current selection and the two flag
    // columns over the fetched flags, so the columns are rebuilt when either
    // moves. The rest are constant either way.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      canApprove,
      canSend,
      selectionRules,
      pageState,
      selection,
      pageRows,
      flagsByInvoice,
      flagsQuery.isPending,
      flagsQuery.isPlaceholderData,
    ],
  );

  // Narrow containers drop the least important columns rather than squeezing
  // Customer to nothing. The container, not the window: the sidebar and split
  // panes change it without a resize.
  const [scrollFrame, frameWidth] = useElementWidth<HTMLDivElement>();
  const renderedWidthsPx = useMemo(
    () =>
      Object.fromEntries(
        columns.flatMap((c) => {
          const id = c.id ?? ('accessorKey' in c ? String(c.accessorKey) : '');
          const px = COLUMN_WIDTHS_PX[id];
          return px === undefined ? [] : [[id, px]];
        }),
      ),
    [columns],
  );
  const hiddenColumns = useMemo(
    () =>
      hiddenColumnsForWidth({
        width: frameWidth,
        fixedPx: renderedWidthsPx,
        hideOrder: HIDE_ORDER,
        flexMinPx: CUSTOMER_MIN_PX,
      }),
    [frameWidth, renderedWidthsPx],
  );
  const columnVisibility = useMemo(
    () => Object.fromEntries([...hiddenColumns].map((id) => [id, false])),
    [hiddenColumns],
  );

  const table = useReactTable({
    data: pageRows,
    columns,
    state: { sorting, columnVisibility },
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      const first = next[0];
      if (!first) {
        // `page: 0` here too. Removing the sort reverts to the default
        // ordering, and staying on page 4 of it shows a slice of a completely
        // different list than the one the user was looking at.
        updateSearch({ sortField: undefined, sortDirection: undefined, page: 0 });
        return;
      }
      updateSearch({
        sortField: first.id as SortField,
        sortDirection: first.desc ? 'desc' : 'asc',
        page: 0,
      });
    },
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
    pageCount: Math.max(1, Math.ceil((query.data?.meta.total ?? 0) / PAGE_SIZE)),
  });

  const total = query.data?.meta.total ?? 0;
  const showingFrom = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const showingTo = Math.min(total, page * PAGE_SIZE + (query.data?.data.length ?? 0));
  const filtered = Boolean(
    q ||
      search.customerIds?.length ||
      search.status ||
      search.invoiceType ||
      search.flagged ||
      search.generationRunId ||
      resolvedRange,
  );

  return (
    <PageShell>
      <PageHeader title="Invoices" subtitle="Invoices raised for your customers." count={total}>
        <Button
          variant="outline"
          onClick={() => exportMutation.mutate()}
          disabled={exportMutation.isPending}
        >
          <Download />
          {exportMutation.isPending ? 'Exporting…' : 'Export'}
        </Button>
        {canEdit ? (
          <Button variant="outline" onClick={() => setGenerateOpen(true)}>
            <Sparkles />
            Generate invoices
          </Button>
        ) : null}
        {canEdit ? (
          <Button asChild>
            <Link to="/invoices/new">
              <Plus />
              New invoice
            </Link>
          </Button>
        ) : null}
        {/* Secondary destinations, not actions on this list — hence a kebab
            rather than more buttons competing with Export and New invoice.
            EVERY item is gated on its own permission, and the kebab itself
            appears only if at least one survives: the two pages behind it are
            reached with very different rights (`invoice.settings.view` is far
            narrower than `invoice.view`, because the remittance block is a
            bank account), so a single gate would either hide the grid from
            people who may use it or lead them straight to a NoAccess. */}
        {canViewInvoiceSettings || canViewVariableExpenses ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              {/* `DropdownMenuTrigger asChild` clobbers this Button's own
                  `data-slot="button"` with its own `data-slot="dropdown-menu-trigger"`
                  (Radix Slot lets the outer wrapper's explicit props win), so the
                  global `[data-slot='button']` pill-radius rule never matches this
                  trigger. Set the radius directly instead of depending on that rule. */}
              <Button
                variant="outline"
                size="icon"
                className="rounded-full"
                aria-label="More actions"
              >
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              {/* The standing door to the month grid, for entering a period's
                  amounts before any panel has appeared. The panel above is the
                  primary route in — this is here so the page stays reachable
                  when there is nothing outstanding. */}
              {canViewVariableExpenses ? (
                <DropdownMenuItem asChild>
                  <Link to="/invoices/variable-expenses">
                    <ReceiptText className="size-4" />
                    Variable expenses
                  </Link>
                </DropdownMenuItem>
              ) : null}
              {/* A diagnostic page, not a daily job — hence the kebab rather
                  than a nav entry, which would imply otherwise. */}
              {canViewInvoiceSettings ? (
                <>
                  <DropdownMenuItem asChild>
                    <Link to="/invoices/runs">
                      <History />
                      Generation runs
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/invoices/settings">
                      <Settings />
                      Invoice settings
                    </Link>
                  </DropdownMenuItem>
                </>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </PageHeader>

      {canEdit ? (
        <GenerateInvoicesSheet
          initialFrequencies={search.gFreqs}
          initialKind={search.gKind}
          initialMonth={search.gMonth}
          open={generateOpen}
          onOpenChange={setGenerateOpen}
          onStarted={setWatchedRunId}
          // So reopening mid-run shows the run rather than an enabled Generate
          // that the one-open-run index must reject.
          liveRunId={watchedRunId}
        />
      ) : null}
      {/* Renders nothing — it drives the progress toast, which is the in-flight
          surface once the dialog is out of the way. Closing the tab loses
          nothing: the run continues server-side and the sweeper finishes it if
          the worker dies.

          Gated on the dialog being CLOSED. While it is open the Generate button
          reports the same run, and mounting both would talk over itself: a
          toast counting up behind a dialog counting the same thing, then two
          places announcing the finish. Exactly one owns the report at a time,
          and the handover is the dialog closing — which is also how the dialog
          reports completion, by closing and letting this raise it. */}
      {watchedRunId !== null && !generateOpen ? (
        <InvoiceRunProgress runId={watchedRunId} onFinished={() => setWatchedRunId(null)} />
      ) : null}

      {/* Keyed to the month the list is filtered by, so the panel answers a
          question about what the user is actually looking at. Renders nothing
          when that month is clean. */}
      <UnbilledCustomersPanel month={unbilledMonth} />

      <div
        data-surface="panel"
        className="flex flex-wrap items-center gap-3 rounded-md border bg-card p-6 py-4"
      >
        <span className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
          <Filter className="size-3.5" />
          Filters:
        </span>
        <SearchInput
          placeholder="Search invoice #, bill to, notes…"
          value={searchInput}
          onValueChange={setSearchInput}
        />
        {/* Multi-select, matching the approvals queue. `lazy` sends the typed
            term to the server so a tenant with more customers than one page
            can still find one by name. Ids are sorted before they hit the URL
            so pick order doesn't fork the query cache. */}
        <div className="min-w-52 max-w-md">
          <EntityPicker
            multiple
            id="invoices-customer-filter"
            config={customerPickerConfig}
            value={search.customerIds ?? []}
            onChange={(ids) =>
              updateSearch({ customerIds: ids.length ? [...ids].sort() : undefined, page: 0 })
            }
            placeholder="All customers"
            searchPlaceholder="Search customers…"
            loadStrategy="lazy"
            popoverWidth="auto"
            badgeOverflow="scroll"
            className="min-h-8"
          />
        </div>
        <DateRangePopover
          value={dateRange}
          presets={DATE_PRESETS}
          onChange={(next) =>
            updateSearch({
              datePreset: next.preset,
              // Custom bounds are only meaningful for the custom preset; a
              // named preset carrying stale from/to would silently narrow the
              // range it claims to describe.
              dateFrom: next.preset === 'custom' ? next.from : undefined,
              dateTo: next.preset === 'custom' ? next.to : undefined,
              page: 0,
            })
          }
        />
        <Select
          value={search.invoiceType ?? ALL}
          onValueChange={(v) =>
            updateSearch({
              invoiceType: v === ALL ? undefined : (v as ListSearch['invoiceType']),
              page: 0,
            })
          }
        >
          <SelectTrigger className="w-40" aria-label="Invoice type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All types</SelectItem>
            {INVOICE_TYPES.map((t) => (
              <SelectItem key={t} value={t}>
                {INVOICE_TYPE_LABELS[t]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {/* Flagged-or-not, which is the whole filter: the reasons a flag
            carries are for reading, not for narrowing. Compiles server-side to
            `reviewFlagCount gt 0` / `eq 0` through the existing DSL. */}
        <Select
          value={search.flagged ?? ALL}
          onValueChange={(v) =>
            updateSearch({
              flagged: v === ALL ? undefined : (v as ListSearch['flagged']),
              page: 0,
            })
          }
        >
          <SelectTrigger className="w-40" aria-label="Review flags">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All invoices</SelectItem>
            <SelectItem value="true">Flagged</SelectItem>
            <SelectItem value="false">Not flagged</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={search.status ?? ALL}
          onValueChange={(v) =>
            updateSearch({ status: v === ALL ? undefined : (v as ListSearch['status']), page: 0 })
          }
        >
          <SelectTrigger className="w-36" aria-label="Status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All statuses</SelectItem>
            {/* Mapped from the registry, like the Type filter above it.
                Hardcoding the three would silently omit a fourth status from
                the filter while the badge — which reads the registry —
                rendered it fine, producing a status nobody could filter to. */}
            {INVOICE_STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {INVOICE_STATUS_LABELS[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {/* The run page's Flagged tile lands here narrowed to ONE run. A run id
            is nothing a user can read or clear in the controls above, so it
            gets its own chip — a filter nobody can see is a list silently
            showing less than they think. */}
        {search.generationRunId ? (
          <Button
            variant="secondary"
            size="sm"
            className="h-8 gap-1"
            onClick={() => updateSearch({ generationRunId: undefined, page: 0 })}
          >
            One generation run
            <X className="size-3.5" />
          </Button>
        ) : null}
        {filtered ? (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 gap-1 text-muted-foreground"
            onClick={() => navigate({ search: {}, replace: true })}
          >
            <X className="size-3.5" />
            Clear
          </Button>
        ) : null}
      </div>

      <StickyTableScroll ref={scrollFrame}>
        {/* `table-fixed` pins the columns to COLUMN_WIDTHS_PX. See the
            comment on that constant for why the auto layout is unusable here.
            The min width only bites once HIDE_ORDER is exhausted: past it the
            table scrolls sideways rather than squeezing Customer to zero. */}
        <Table
          className="table-fixed"
          style={{
            minWidth: minTableWidthPx({
              fixedPx: renderedWidthsPx,
              hidden: hiddenColumns,
              flexMinPx: CUSTOMER_MIN_PX,
            }),
          }}
        >
          <TableHeader>
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id}>
                {hg.headers.map((h) => {
                  const dir = h.column.getIsSorted();
                  return (
                    <TableHead key={h.id} style={{ width: COLUMN_WIDTHS_PX[h.column.id] }}>
                      {h.column.getCanSort() ? (
                        <button
                          type="button"
                          className="inline-flex select-none items-center gap-1 hover:text-foreground"
                          onClick={h.column.getToggleSortingHandler()}
                        >
                          {flexRender(h.column.columnDef.header, h.getContext())}
                          {dir === 'asc' ? (
                            <ArrowUp className="size-3" />
                          ) : dir === 'desc' ? (
                            <ArrowDown className="size-3" />
                          ) : (
                            <ArrowUpDown className="size-3 opacity-40" />
                          )}
                        </button>
                      ) : (
                        flexRender(h.column.columnDef.header, h.getContext())
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody aria-busy={query.isLoading}>
            {query.isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  {table.getVisibleLeafColumns().map((_c, j) => (
                    <TableCell key={`skeleton-cell-${i}-${j}`}>
                      <Skeleton className="h-4 w-20" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : query.isError ? (
              // A destructive banner rather than an empty table: "failed" and
              // "no invoices yet" are different answers and must not look the
              // same (G15). The trace id is what support can search on.
              <TableRow>
                <TableCell
                  colSpan={table.getVisibleLeafColumns().length}
                  className="py-10 text-center"
                >
                  <p className="text-destructive">
                    {query.error instanceof ApiError
                      ? query.error.message
                      : 'Failed to load invoices.'}
                  </p>
                  {query.error instanceof ApiError && query.error.traceId ? (
                    <p className="mt-1 text-xs text-muted-foreground">
                      Trace ID: {query.error.traceId}
                    </p>
                  ) : null}
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-3"
                    onClick={() => query.refetch()}
                  >
                    Retry
                  </Button>
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={table.getVisibleLeafColumns().length}
                  className="py-10 text-center text-muted-foreground"
                >
                  {filtered
                    ? 'No invoices match these filters.'
                    : 'No invoices yet. They appear here once raised.'}
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((r) => (
                <TableRow key={r.id}>
                  {r.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell ?? defaultCell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </StickyTableScroll>

      {canApprove || canSend ? (
        <BulkActionBar
          open={selectionActive}
          emphasis={selection.mode === 'filter'}
          busy={bulkMutation.isPending || bulkSend.isPending}
          busyAction={
            bulkMutation.isPending
              ? bulkMutation.variables.action
              : bulkSend.isPending
                ? 'send'
                : undefined
          }
          onClear={() => setSelection(NOTHING_SELECTED)}
          summary={
            selection.mode === 'filter'
              ? `All ${selectedCount.toLocaleString()} matching invoices selected`
              : `${selectedCount} ${selectedCount === 1 ? 'invoice' : 'invoices'} selected`
          }
          detail={
            selection.mode === 'filter' ? (
              <>
                Selection follows the filters, not the page.{' '}
                <button type="button" className="underline underline-offset-2" onClick={selectPage}>
                  Select only this page
                </button>
              </>
            ) : pageState === 'all' && total > pageRows.length ? (
              <>
                Every selectable invoice on this page.{' '}
                <button
                  type="button"
                  className="underline underline-offset-2"
                  onClick={() => setSelection({ mode: 'filter', excluded: new Map() })}
                >
                  {/* The ACTIONABLE subtotal, not `total`. Quoting the whole
                      matching set here and then a draft+approved count in the
                      summary the instant it is clicked drops the sent invoices
                      out of the number with nothing on screen to explain it. */}
                  {matchingActionable === undefined
                    ? 'Select all matching these filters'
                    : `Select all ${matchingActionable.toLocaleString()} matching these filters`}
                </button>
              </>
            ) : canSend ? (
              'Sent invoices can be selected to send again.'
            ) : (
              'Sent invoices can’t be selected — that state is final.'
            )
          }
          actions={buildBulkActions({
            approvableCount: canApprove ? approvableCount : null,
            onApprove: () => bulkMutation.mutate({ action: 'approve', target: targetFor('draft') }),
            // Rescheduling and re-dating are edits, gated like the editor's
            // fields — not an approval.
            schedulableCount: canEdit ? schedulableCount : null,
            onSchedule: () => setScheduleOpen(true),
            // Drafts only: the invoice date is content, locked by approval.
            redatableCount: canEdit ? approvableCount : null,
            onInvoiceDate: () => setInvoiceDateOpen(true),
            sendableCount: canSend ? sendableCount : null,
            onSend: () => setBulkSendOpen(true),
          })}
        />
      ) : null}

      <InvoiceDateDialog
        open={invoiceDateOpen}
        count={approvableCount}
        target={targetFor('draft')}
        busy={bulkMutation.isPending}
        onConfirm={(invoiceDate) =>
          bulkMutation.mutate(
            { action: 'invoiceDate', target: targetFor('draft'), invoiceDate },
            { onSettled: () => setInvoiceDateOpen(false) },
          )
        }
        onCancel={() => setInvoiceDateOpen(false)}
      />

      <InvoiceScheduleSendDialog
        open={scheduleOpen}
        count={schedulableCount}
        busy={bulkMutation.isPending}
        onConfirm={(schedule) =>
          bulkMutation.mutate(
            { action: 'schedule', target: targetSchedulable(), schedule },
            { onSettled: () => setScheduleOpen(false) },
          )
        }
        onCancel={() => setScheduleOpen(false)}
      />

      {canSend ? (
        <InvoiceBulkSendDialog
          open={bulkSendOpen}
          target={targetSendable()}
          busy={bulkSend.isPending}
          onConfirm={() => bulkSend.mutate(targetSendable())}
          onCancel={() => setBulkSendOpen(false)}
        />
      ) : null}

      {sendRow ? (
        <InvoiceSendDialog
          invoice={sendRow}
          open
          onOpenChange={(next) => {
            if (!next) setSendRow(null);
          }}
        />
      ) : null}

      <footer className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {total === 0 ? 'No results' : `Showing ${showingFrom}–${showingTo} of ${total}`}
        </span>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => updateSearch({ page: Math.max(0, page - 1) })}
            disabled={page === 0 || query.isFetching}
          >
            Previous
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => updateSearch({ page: page + 1 })}
            disabled={(page + 1) * PAGE_SIZE >= total || query.isFetching}
          >
            Next
          </Button>
        </div>
      </footer>
    </PageShell>
  );
}

/**
 * The actions the bar offers.
 *
 * Approve fires on the click: the button is the whole message — the verb and
 * the exact number of invoices it moves. Invoice date and Send date open a
 * dialog, because each needs a choice before there is anything to do.
 *
 * There is deliberately no Unlock: it clears a sign-off, so it is done one
 * invoice at a time from that invoice's own screen.
 */
function buildBulkActions({
  approvableCount,
  schedulableCount,
  redatableCount,
  sendableCount,
  onApprove,
  onSchedule,
  onInvoiceDate,
  onSend,
}: {
  /** Null when the caller may not approve: the action is left out. */
  approvableCount: number | null;
  /** Null when the caller may not edit invoices: the action is left out. */
  schedulableCount: number | null;
  /** Drafts selected; null when the caller may not edit invoices. */
  redatableCount: number | null;
  /** Approved and sent selected; null when the caller may not send. */
  sendableCount: number | null;
  onApprove: () => void;
  onSchedule: () => void;
  onInvoiceDate: () => void;
  onSend: () => void;
}): BulkAction[] {
  return [
    ...(redatableCount === null
      ? []
      : [
          {
            id: 'invoiceDate',
            label: 'Invoice date',
            variant: 'outline',
            icon: <CalendarDays className="size-4" />,
            count: redatableCount,
            onAct: onInvoiceDate,
          } satisfies BulkAction,
        ]),
    ...(schedulableCount === null
      ? []
      : [
          {
            id: 'schedule',
            label: 'Send date',
            variant: 'outline',
            icon: <CalendarClock className="size-4" />,
            count: schedulableCount,
            onAct: onSchedule,
          } satisfies BulkAction,
        ]),
    ...(sendableCount === null
      ? []
      : [
          {
            id: 'send',
            label: 'Send',
            variant: 'outline',
            icon: <Send className="size-4" />,
            count: sendableCount,
            onAct: onSend,
          } satisfies BulkAction,
        ]),
    ...(approvableCount === null
      ? []
      : [
          {
            id: 'approve',
            label: 'Approve',
            icon: <CheckCheck className="size-4" />,
            count: approvableCount,
            onAct: onApprove,
          } satisfies BulkAction,
        ]),
  ];
}

/** A `YYYY-MM-DD` column. Rendered from the string's own parts rather than
 *  through `new Date(iso)`, which parses a bare date as UTC midnight and then
 *  displays it a day early for anyone west of Greenwich. */
function DateCell({ iso }: { iso: string | null }) {
  if (!iso) return <span className="text-xs text-muted-foreground">—</span>;
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return <span className="text-xs text-destructive">Invalid</span>;
  return <span className="text-sm tabular-nums">{`${m}/${d}/${y}`}</span>;
}

const defaultCell: ColumnDef<Invoice>['cell'] = ({ getValue }) => String(getValue() ?? '');

/** Keep the URL to what differs from the defaults, so a bookmarked link stays
 *  legible and a cleared filter actually leaves the address bar. */
function stripDefaults(s: ListSearch): ListSearch {
  const out: ListSearch = {};
  if (s.q) out.q = s.q;
  if (s.customerIds?.length) out.customerIds = s.customerIds;
  if (s.status) out.status = s.status;
  if (s.invoiceType) out.invoiceType = s.invoiceType;
  if (s.flagged) out.flagged = s.flagged;
  if (s.generationRunId) out.generationRunId = s.generationRunId;
  if (s.datePreset && s.datePreset !== 'all_time') out.datePreset = s.datePreset;
  if (s.dateFrom) out.dateFrom = s.dateFrom;
  if (s.dateTo) out.dateTo = s.dateTo;
  if (s.page && s.page > 0) out.page = s.page;
  if (s.sortField && s.sortField !== DEFAULT_SORT_FIELD) out.sortField = s.sortField;
  if (s.sortDirection && s.sortDirection !== DEFAULT_SORT_DIRECTION) {
    out.sortDirection = s.sortDirection;
  }
  // Carried, not defaulted away: this rebuilds the search from scratch, so a
  // filter change while the generate sheet is open would otherwise drop it out
  // of the URL and close the sheet under the user.
  if (s.generate) out.generate = s.generate;
  if (s.gMonth) out.gMonth = s.gMonth;
  if (s.gFreqs) out.gFreqs = s.gFreqs;
  if (s.gKind) out.gKind = s.gKind;
  return out;
}

/**
 * The previous invoice's total, and how far this one moved.
 *
 * "No prior" is written out rather than left blank or rendered as `$0.00`: a
 * blank cell reads as a load failure and a zero reads as a real amount that
 * would imply a 100% swing (G15). The delta is only shown when there is
 * something to compare against.
 */
function PreviousCell({ invoice }: { invoice: Invoice }) {
  if (invoice.priorTotal === null) {
    return <span className="text-muted-foreground text-xs">No prior</span>;
  }
  const formatted = formatMoney(invoice.priorTotal, invoice.currency);
  if (formatted === null) return <span className="text-destructive text-xs">Unknown</span>;

  const delta = deltaLabel(invoice.priorTotal, invoice.total);
  return (
    <span className="text-muted-foreground text-sm tabular-nums">
      {formatted}
      {delta === null ? null : (
        <span
          className={cn('ml-1.5 text-xs', delta.moved ? 'text-amber-600 dark:text-amber-400' : '')}
        >
          {delta.text}
        </span>
      )}
    </span>
  );
}

/**
 * Signed change from the prior total, as a percentage.
 *
 * Every step goes through the shared money helpers. `Number()` must not touch
 * these: money is decimal and binary floating point cannot represent most
 * cents (G15), and — the trap that was actually here — `Number('')` is `0`
 * rather than `NaN`, so a blank prior total passed a finite check and rendered
 * the confident claim "new". `isMoney` rejects it, along with the values
 * `Number` silently accepts (`' 5000 '`, `'0x10'`, `'1e3'`).
 *
 * `percentChangeOfMoney` is the SAME function the server used to compute
 * `AMOUNT_CHANGED.facts.deltaPct`, at the same scale, so this column and the
 * popover beside it cannot report different percentages for one pair of
 * amounts. It returns null both for unreadable input and for a zero base, so
 * `isMoney` is checked first and a null after that can only mean the base was
 * zero — "new" rather than "unreadable".
 */
function deltaLabel(prior: string, current: string): { text: string; moved: boolean } | null {
  if (!isMoney(prior) || !isMoney(current)) return null;
  if (compareMoney(prior, current) === 0) return { text: '—', moved: false };
  const pct = percentChangeOfMoney(prior, current);
  if (pct === null) return { text: 'new', moved: true };
  return { text: `${pct.startsWith('-') ? '' : '+'}${pct}%`, moved: true };
}

/**
 * The flag count, with the reasons behind it in a popover.
 *
 * A count in the column and the reasons on demand: spelling every reason out
 * inline would make the column too wide to survive on a laptop, and most rows
 * have none. Rendered through the shared `InvoiceFlagDetails` so the popover
 * and the invoice's own review panel cannot word the same flag differently.
 */
function FlagsCell({
  invoice,
  flags,
  /** Whether the page's flag request is still outstanding. Load and failure
   *  look identical from an empty array, and `InvoiceFlagDetails` reports an
   *  empty array as a failure — so without this a popover opened during a
   *  perfectly normal load claims the flags could not be fetched. */
  pending,
}: {
  invoice: Invoice;
  flags: InvoiceFlag[];
  pending: boolean;
}) {
  if (invoice.reviewFlagCount <= 0) {
    return <span className="text-muted-foreground text-xs">—</span>;
  }
  const peak = peakInvoiceFlagSeverity(flags.map((f) => f.severity));
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="rounded focus-visible:outline-2 focus-visible:outline-ring"
          aria-label={`${invoice.reviewFlagCount} review flags on ${invoice.invoiceNumber}`}
        >
          <InvoiceFlagBadge count={invoice.reviewFlagCount} severity={peak} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80">
        <p className="mb-2 font-medium text-muted-foreground text-xs uppercase tracking-wide">
          Flagged for review
        </p>
        {pending && flags.length === 0 ? (
          <p className="text-muted-foreground text-xs">Loading…</p>
        ) : (
          <InvoiceFlagDetails flags={flags} />
        )}
        {invoice.priorInvoiceId === null ? null : (
          <Link
            to="/invoices/$id"
            params={{ id: invoice.priorInvoiceId }}
            className="mt-3 block text-primary text-xs hover:underline"
          >
            Compare with the previous invoice
          </Link>
        )}
      </PopoverContent>
    </Popover>
  );
}
